import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import downloadAndApplyFontFamilyOperation from 'editor/src/store/design/operation/downloadAndApplyFontFamilyOperation';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import CollapsableContainer from 'editor/src/component/CollapsableContainer';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

import UploadedFontItem from './UploadedFontItem';

import styles from './index.module.scss';

// If the host never tells us about the customer's fonts, stop claiming to be
// loading them rather than waiting forever.
const LOADING_TIMEOUT_MS = 10000;

// Enough placeholder rows to fill the visible part of the list, so the section
// reads as the list itself arriving rather than a spinner that gets replaced.
const SKELETON_ROW_COUNT = 6;

interface Props {
  currentFontFile: string | undefined;
  searchQuery?: string;
}

function UploadedFontsSection({ currentFontFile, searchQuery }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const uploadedFonts = useSelector((state) => state.fonts.uploadedFonts);
  const uploadedFontsReceived = useSelector((state) => state.fonts.uploadedFontsReceived);
  const fontUploadEnabled = useSelector((state) => state.fonts.fontUploadEnabled);
  const isMobile = useIsMobile();
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  // The customer's fonts arrive from the host after the editor has rendered.
  // Until then the section says it is loading, so a customer with a large
  // library sees the section exists instead of it appearing out of nowhere.
  const isLoading = fontUploadEnabled && !uploadedFontsReceived && !loadingTimedOut;

  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }
    const timer = window.setTimeout(() => setLoadingTimedOut(true), LOADING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isLoading]);

  const filteredFonts = useMemo(() => {
    if (!searchQuery) {
      return uploadedFonts;
    }
    const query = searchQuery.toLowerCase();
    return uploadedFonts.filter(
      (font) => font.name.toLowerCase().includes(query) || font.family?.toLowerCase().includes(query),
    );
  }, [uploadedFonts, searchQuery]);

  const onSelect = useCallback(
    (fontFile: string) => {
      void dispatch(downloadAndApplyFontFamilyOperation(fontFile));
      sendPostMessage('fonts.premiumFontSelected', undefined);
    },
    [dispatch],
  );

  // TODO: Re-enable when delete API is ready (Phase 2)
  // const onDelete = useCallback(
  //   (fontFile: string) => {
  //     dispatch(
  //       openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
  //         popupTitle: t('txt_editor_font_delete_title'),
  //         textLines: [t('txt_editor_font_delete_confirm')],
  //         hideTitleIcon: true,
  //         options: [
  //           { title: t('txt_editor_font_delete_cancel') },
  //           {
  //             title: t('txt_editor_font_delete_action'),
  //             onConfirm: () => {
  //               sendPostMessage('fonts.deleteRequested', { fontFile });
  //             },
  //           },
  //         ],
  //       }),
  //     );
  //   },
  //   [dispatch, t],
  // );

  if (filteredFonts.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className={styles.uploadedFontsSection}>
      <CollapsableContainer
        className={styles.collapsableContainer}
        headerClassName={styles.collapsableHeader}
        title={t('txt_editor_font_uploaded_section')}
        defaultExpand
      >
        {/* On mobile the tab is a full-height drawer that scrolls itself, so a
            nested scroller there would just steal the drawer's scrolling. */}
        <div className={cn(styles.fontList, { [styles.scrollable]: !isMobile }, 'cy-uploaded-fonts')}>
          {filteredFonts.map((font) => (
            <UploadedFontItem
              key={font.fontFile}
              font={font}
              isActive={font.fontFile === currentFontFile}
              onSelect={onSelect}
            />
          ))}
          {/* Placeholders sit in the same list at the same row height, so rows
              replace them in place instead of the section being swapped out. */}
          {isLoading &&
            Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <div key={`skeleton-${index}`} className={cn(styles.skeletonRow, 'cy-uploaded-fonts-loading')}>
                <span className={styles.skeletonBar} style={{ width: `${70 - (index % 3) * 12}%` }} />
              </div>
            ))}
        </div>
      </CollapsableContainer>
    </div>
  );
}

export default React.memo(UploadedFontsSection);
